/**
 * Score job queue — decouples workout submission from score computation.
 *
 * Instead of calling compute-score Edge Function synchronously on every
 * workout save, we enqueue a job. The Edge Function processes the queue.
 * This prevents write contention under load and makes the UI instantly responsive.
 *
 * At current scale (<1K users): jobs are processed immediately after enqueue.
 * At scale (>10K users): switch PROCESS_IMMEDIATELY to false and run a cron worker.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('SCORE_QUEUE');

// At current scale, process immediately after enqueue (synchronous fallback).
// Set to false when you have a dedicated queue worker running.
const PROCESS_IMMEDIATELY = true;

export type ScoreTrigger = 'workout_save' | 'workout_delete' | 'manual';

/**
 * Enqueue a score recompute job for a user.
 * If PROCESS_IMMEDIATELY is true, also fires the Edge Function right away.
 */
export async function enqueueScoreJob(userId: string, trigger: ScoreTrigger = 'workout_save'): Promise<void> {
  try {
    // Dedup: skip insert if a pending job already exists for this user
    const { data: existing } = await supabase
      .from('score_jobs')
      .select('id')
      .eq('user_id', userId)
      .eq('status', 'pending')
      .limit(1);

    if (!existing || existing.length === 0) {
      const { error } = await supabase
        .from('score_jobs')
        .insert({ user_id: userId, trigger, status: 'pending' });
      if (error) {
        log.warn('Failed to insert score job (non-blocking):', error.message);
      }
    }

    if (PROCESS_IMMEDIATELY) {
      await processScoreJobNow(userId, trigger);
    }
  } catch (err) {
    log.error('enqueueScoreJob error:', err);
  }
}

/**
 * Call the compute-score Edge Function directly and mark the job done.
 */
export async function processScoreJobNow(userId: string, trigger: ScoreTrigger = 'manual'): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('compute-score', {
      body: { user_id: userId, trigger },
    });
    if (error) {
      log.warn('compute-score edge function error:', error.message);
      // Mark failed so it doesn't stay pending
      await supabase
        .from('score_jobs')
        .update({ status: 'failed', last_error: error.message, completed_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('status', 'pending');
      return;
    }
    // Mark done
    await supabase
      .from('score_jobs')
      .update({ status: 'done', completed_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('status', 'pending');
  } catch (err: any) {
    log.error('processScoreJobNow error:', err);
  }
}
